<template>
  <div class="calendar container-fluid">
    <div class="row">
        <div class="col">
            <div class="calendar-toolbar d-flex align-items-center jumbotron mb-4 py-4" v-if="calendarInstance">

                <h2 class="m-0">{{ visibleDate }}</h2>

                <div class="flex-grow-1 justify-content-end d-flex align-items-center">                    
                    <b-button
                        class="mr-2"
                        type="button"
                        variant="outline-primary"
                        @click="toggleTypeManager"
                    >
                        {{ $t(`planning.createType`) }}
                    </b-button>

                    <general-select
                        class="calendar-type-select"
                        placeholder="Selecteer een type"
                        :options="calendarTypeOptions"
                        v-model="selectedCalendarType"
                        @input="setCalendarType"
                    />
        
                    <div class="vr mx-4"></div>

                    <b-button
                        class="mr-2"
                        type="button"
                        variant="outline-primary"
                        v-b-tooltip.hover
                        title="Verander kalender"
                        @click="viewToggle"
                    >
                        <i class="fa fa-calendar"></i> - 
                       {{ $t(`planning.${calendarView}`) }}
                    </b-button>

                    <b-button type="button"
                        class="mr-1"
                        variant="outline-secondary"
                        v-b-tooltip.hover
                        title="Ga naar vandaag"
                        @click="moveToday"
                    >
                        <i class="fa fa-calendar"></i>
                    </b-button>

                    <b-button type="button"
                        class="mr-1"
                        variant="outline-secondary"
                        v-b-tooltip.hover
                        :title="`Vorige ${$t(`planning.${calendarView}`)}`"
                        @click="movePrev"
                    >
                        <i class="fa fa-angle-left"></i>
                    </b-button>

                    <b-button type="button"
                        variant="outline-secondary"
                        v-b-tooltip.hover
                        :title="`Volgende ${$t(`planning.${calendarView}`)}`"
                        @click="moveNext"
                    >
                        <i class="fa fa-angle-right"></i>
                    </b-button>
                </div>
            </div>

            <div class="calendar-container jumbotron py-4">
                <calendar
                    ref="calendar"
                    :calendars="calendarTypes"
                    :schedules="selectedCalendarSchedules"
                    :view="calendarView"
                    :week="week"
                    :month="month"
                    :useCreationPopup="false"
                    :useDetailPopup="false"
                    :usageStatistics="false"
                    :taskView="false"
                    defaultView="month"
                    @beforeCreateSchedule="beforeCreate"
                    @beforeUpdateSchedule="beforeUpdate"
                    @clickSchedule="clickSchedule"
                    disabled
                />
            </div>
        </div>

        <template v-if="mode == 'typeManager'">
            <div class="col-4">
                <type-manager 
                    :calendarTypes="calendarTypes" 
                    @update="updatedTypes"
                    @close="mode = 'calendar'"
                />
            </div>
        </template>

        <template v-else-if="mode == 'timeSlot'">
            <div class="col-4">
                <timeslot
                    :selectedTime="selectedTime"
                    :selectedSchedule="selectedSchedule"
                    :calendarTypes="calendarTypes"
                    @close="closeAppointment"
                    @update="updatedTimeSlot"
                    @delete="deletedTimeSlot"
                />
            </div>
        </template>
    </div>
  </div>
</template>

<script>
import TypeManager from './TypeManager';
import GeneralSelect from '../../elements/Input/GeneralSelect';
import LoaderSpinner from '../../elements/LoaderSpinner';
import Timeslot from "./Timeslot";
import * as moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import { Calendar } from "@toast-ui/vue-calendar";
import "tui-calendar/dist/tui-calendar.css";
import "tui-calendar/dist/tui-calendar.css";

export default {
    components: {
        Calendar,
        Timeslot,
        LoaderSpinner,
        GeneralSelect,
        TypeManager
    },
    data: function () { return {
        availableViews: ['day', 'week', 'month'],
        calendarInstance: null,
        selectedSchedule: null,
        selectedTime: null,
        selectedCalendarType: 'all',
        timeSlotOpen: false,
        calendarSchedules: [],
        selectedCalendarSchedules: [],
        typeManagerOpen: false,
        mode: 'calendar',
        calendarView: "week",
        scheduleView: ["time"],
        calendarTypes: [],
        calendarTypeOptions: [{
            value: 'all',
            label: 'Alles weergeven',
            indicatorColor: 'white',
        }],
        week: { 
            startDayOfWeek: 1,
            daynames: ['Zon', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'], 
        },
        month: { 
            startDayOfWeek: 1, 
            daynames: ['Zon', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'], 
        },
    }},
    computed: {
        ...mapGetters([
            'isFetchingTimeSlots',
            'isUpdatingTimeSlot',
        ]),

        visibleDate: function () {
            return this.capitalize(new Date(this.calendarInstance.getDate()._date).toLocaleString(this.$i18n.locale, { month: 'long', year: 'numeric' }));
        }
    },
    methods: {
        ...mapActions([
            'getTimeSlots',
            'updateTimeSlot',
        ]),

        viewToggle() {
            const currentIndex = this.availableViews.indexOf(this.calendarView);

            if (currentIndex == this.availableViews.length - 1)
                this.calendarView = this.availableViews[0];
            else
                this.calendarView = this.availableViews[currentIndex + 1];
        },

        moveToday() {
            this.calendarInstance.today();
        },

        movePrev() {
            this.calendarInstance.prev();
        },

        moveNext() {
            this.calendarInstance.next();
        },

        beforeCreate(e) {
            if (this.calendarView == 'month') {
                this.calendarView = 'week';
                this.calendarInstance.setDate(e.start._date);

                return;
            }

            if (this.selectedSchedule) {
                e.guide.clearGuideElement();
            }

            this.mode = 'timeSlot';
            this.selectedTime = { start: e.start._date.toISOString(), end: e.end._date.toISOString() };
        },

        async beforeUpdate(e) {
            try {
                await this.updateTimeSlot({
                    id: e.schedule.id,
                    params: {
                        ...e.changes.start && { start: moment(e.changes.start._date).toISOString() },
                        ...e.changes.end   && { end: moment(e.changes.end._date).toISOString() }
                    }
                })

                await this.fetchTimeSlots();

                this.selectedTime = null;
                this.selectedSchedule = this.calendarSchedules.find(el => el.id == e.schedule.id);
            }
            catch (error) {
                console.log(error);                
            }
        },

        clickSchedule(e) {
            if (this.selectedSchedule && this.selectedSchedule.id == e.schedule.id) {
                return this.closeAppointment()
            }

            this.mode = 'timeSlot';
            this.selectedSchedule = e.schedule;
        },

        toggleTypeManager() {
            if (this.mode == 'typeManager') this.mode = 'calendar';
            else {
                this.selectedSchedule = null;
                this.selectedTime = null;
                this.mode = 'typeManager'
            };
        },

        closeAppointment() {
            this.mode = 'calendar';
            this.selectedSchedule = null;
            this.selectedTime = null;
        },

        async deletedTimeSlot() {
            try {
                await this.fetchTimeSlots();

                this.closeAppointment();
            }
            catch (error) {
                console.log(error);
            }
        },

        async updatedTimeSlot(id) {
            try {
                await this.fetchTimeSlots();
                
                this.selectedTime = null;
                this.selectedSchedule = this.calendarSchedules.find(el => el.id == id);
            }
            catch (error) {
                console.log(error);
            }
        },

        async updatedTypes() {
            try {
                await this.fetchTimeSlots();
                
                this.selectedTime = null;
            }
            catch (error) {
                console.log(error);
            }
        },

        async fetchTimeSlots() {
            try {
                const timeSlots = await this.getTimeSlots();
                
                this.calendarTypes = timeSlots.types.map(el => {
                    return {
                        id: el.id.toString(),
                        name: el.name,
                        color: 'white',
                        bgColor: el.color,
                        borderColor: '#007bff',
                        email_note: el.email_note,
                    }
                });

                this.calendarTypeOptions = [
                    {
                        value: 'all',
                        label: 'Alles weergeven',
                        indicatorColor: 'white',
                    },
                    ...this.calendarTypes.map(el => {
                        return {
                            value: el.id,
                            label: el.name,
                            indicatorColor: el.bgColor,
                        }
                    })
                ];

                this.calendarSchedules = timeSlots.time_slots.map(el => {
                    const type = timeSlots.types.find(type => type.id == el.type);

                    return {
                        id: el.id,
                        title: `${type.name} (${el.appointments.length}/${el.max_appointments})`,
                        calendarId: type ? type.id.toString() : '0',
                        category: 'time',
                        start: el.start,
                        end: el.end,
                        raw: { ...el },
                    }
                });

                this.setCalendarType();
            }
            catch (error) {
                console.log(error);
            }
        },

        setCalendarType() {
            this.selectedCalendarSchedules = this.calendarSchedules.filter(el => 
                this.selectedCalendarType == 'all' ||  el.calendarId == this.selectedCalendarType
            )
        }
    },
    created() {
        this.fetchTimeSlots();
    },
    mounted() {
        this.calendarInstance = this.$refs.calendar.calendarInstance;
    }
};
</script>

<style lang="scss">
.vr {
    border-left: 2px solid rgba(#000, 0.1);
    height: 2.5rem;
}

.calendar {
    &-container {
        position: relative;
    }

    &-loading-mask {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(#000, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &-type-select {
        width: 200px;

        .placeholder, .caret {
            color: rgba(#000, 0.5) !important;
        }
    }
}
</style>



// WEBPACK FOOTER //
// Main.vue?6b807f82